namespace $ {

	type $shm_hitalama_map_migration_step = {
		version: number
		migrate: ( map: $shm_hitalama_map )=> void
	}

	export class $shm_hitalama_map_migration {

		static migrations(): readonly $shm_hitalama_map_migration_step[] {
			return [
				{
					version: 1,
					migrate: map => {
						this.migrate_widgets_link( map )
						this.migrate_widgets_text( map )
					},
				},
				{
					version: 2,
					migrate: map => {
						this.migrate_widgets_entities( map )
					},
				},
				{
					version: 3,
					migrate: map => {
						this.migrate_chart_settings_widgets( map )
					},
				},
				{
					version: 4,
					migrate: map => {
						this.migrate_method_runtime_this( map )
					},
				},
				{
					version: 5,
					migrate: map => {
						this.migrate_inside_widget_to_container_widget( map )
					},
				},
				{
					version: 6,
					migrate: map => {
						this.migrate_widget_border_styles( map )
					},
				},
				{
					version: 7,
					migrate: map => {
						this.migrate_image_widgets( map )
					},
				},
				{
					version: 8,
					migrate: map => {
						this.migrate_runtime_context_dollar( map )
					},
				},
				{
					version: 9,
					migrate: map => {
						this.migrate_opencode_session_ids( map )
					},
				},
				{
					version: 10,
					migrate: map => {
						this.migrate_dashboard_scoped_pb( map )
					},
				},
				{
					version: 11,
					migrate: map => {
						this.migrate_table2_custom_field_codes( map )
					},
				},
				{
					version: 12,
					migrate: map => {
						this.migrate_table2_dataset_maps( map )
					},
				},
				{
					version: 13,
					migrate: map => {
						this.migrate_history_land( map )
					},
				},
				{
					version: 14,
					migrate: map => {
						this.migrate_history_storage( map )
					},
				},
				{
					version: 15,
					migrate: map => {
						this.migrate_history_storage_meta( map )
					},
				},
				{
					version: 16,
					migrate: map => {
						this.migrate_history_reference( map )
					},
				},
				{
					version: 17,
					migrate: map => {
						map.widget_relations_repair()
					},
				},
			]
		}

		static latest_version() {
			return this.migrations().at( -1 )?.version ?? 0
		}

		@ $mol_action
		static migrate( map: $shm_hitalama_map ) {
			const migrations = this.migrations()
			const target_index = migrations.length - 1
			if( target_index < 0 ) return map.version()

			const version = map.version()
			let version_index = version ? migrations.findIndex( migration => migration.version === version ) : -1
			if( version_index < 0 && version ) throw new Error( `Unknown hitalama map version "${ version }"` )
			if( version_index >= target_index ) return version
			return $shm_hitalama_map_history.silence( ()=> {

			for( let next_index = version_index + 1; next_index <= target_index; ++next_index ) {
				const migration = migrations[ next_index ]
				if( !migration ) throw new Error( `Missing hitalama map migration to version index "${ next_index }"` )

				migration.migrate( map )
				const next_version = map.version( migration.version )
				if( next_version !== migration.version ) {
					throw new Error( `Hitalama map migration expected version "${ migration.version }", got "${ next_version }"` )
				}
			}

			return map.version()
			} )
		}

		@ $mol_action
		static migrate_widgets_link( map: $shm_hitalama_map ) {
			const old_list = map.Widgets()?.cast( $giper_baza_list_link.to( ()=> $shm_hitalama_map_widget ) )
			const links = old_list?.items()

			if( links && links.length > 2 ) {
				const area = map.land().area_make()
				const list = $giper_baza_glob.Pawn( area.link(), $giper_baza_list_link.to( ()=> $shm_hitalama_map_widget ) )
				list.items( links )
				map.Widgets(null)?.remote( list )
			}
		}

		@ $mol_action
		static migrate_widgets_text( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				const text = widget.Text()?.value()
				if( text !== undefined ) widget.Text_atom(null)?.val( text )

				widget.Text_crdt_disabled(null)?.val( true )
				widget.has( 'Text', false )
			}
		}

		static widgets_all( map: $shm_hitalama_map ) {
			const widgets = [
				... ( map.Widgets_list?.()?.remote_list() ?? [] ),
				... ( map.Widgets_removed_list?.()?.remote_list() ?? [] ),
			]
			const migrated = new Set< string >()

			return widgets.filter( widget => {
				const link = widget.link_str()
				if( migrated.has( link ) ) return false
				migrated.add( link )
				return true
			} )
		}

		@ $mol_action
		static migrate_dashboard_scoped_pb( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				const code = widget.code()
				if( !code.includes( 'const ScopedPb = class extends $.$shm_ba_pb' ) ) continue

				const next = code
					.replace(
						'\tstatic _realtime = new Map',
						[
							'\tstatic _realtime = { source: null, client_id: "", topics: new Set }',
							'\tstatic _realtime_global = new Set',
							'\tstatic _realtime_filtered = new Set',
							'\tstatic _realtime_live = new Set',
							'\tstatic _realtime_cache_pending = new WeakSet',
						].join( '\n' ),
					)
					.replace(
						[
							'\t\tfor( const source of this._realtime.values() ) source.close()',
							'\t\tthis._realtime.clear()',
						].join( '\n' ),
						'\t\tthis.realtime_reset()',
					)
					.replace( '\t\t\tpromise => promise,', '\t\t\tpromise => { throw promise },' )
					.replace(
						[
							'\t\tif( !result || $mol_promise_like( result ) ) return false',
							'\t\treturn result',
						].join( '\n' ),
						'\t\treturn Boolean( result )',
					)
					.replace(
						'const scoped = $.$mol_ambient({ $shm_ba_pb: ScopedPb })',
						'const scoped = $.$mol_ambient({ $shm_pb: ScopedPb, $shm_ba_pb: ScopedPb })',
					)

				if( next !== code ) widget.code( next )
			}
		}

		@ $mol_action
		static migrate_table2_custom_field_codes( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				if( widget.type() !== 'table2' ) continue
				const dataset = widget.Dataset()?.remote()
				const table = widget.Table()?.remote()
				if( !dataset || !table ) continue
				for( const field of dataset.fields() ) {
					if( field.type() !== 'custom' || field.custom_code() ) continue
					const code = table.col_custom_code( field.title() )
					if( code ) field.custom_code( code )
				}
			}
		}

		@ $mol_action
		static migrate_table2_dataset_maps( map: $shm_hitalama_map ) {
			for( const dataset of map.Datasets?.()?.remote_list() ?? [] ) dataset.map_bind( map )
		}

		@ $mol_action
		static migrate_history_land( map: $shm_hitalama_map ) {
			const pending_cleanup = map.History_legacy()?.remote()
			const slot = map.History()
			const legacy = slot?.remote()
			// The cleanup pointer is published before the replacement. A retry
			// between those writes must finish copying, not erase the active history.
			if( pending_cleanup && legacy && legacy.link().str !== pending_cleanup.link().str ) {
				this.clear_legacy_history( pending_cleanup )
				legacy.storage_migrated( true )
				map.History_legacy( null )?.remote( null )
				return
			}

			if( !legacy ) return

			if( legacy.land().link().str !== map.land().link().str ) {
				legacy.map_bind( map )
				return
			}

			const history_land = map.land().area_make( $mol_hash_string( slot!.link().str + ':history' ) )
			const history = history_land.Data( $shm_hitalama_map_history )
			history.map_bind( map )

			const sources = $mol_wire_sync( this ).history_migration_snapshot( legacy )
			const relocated = new Map< string, string >
			for( const source of sources ) {
				const origin = source.data.origin && ( relocated.get( source.data.origin ) ?? source.data.origin )
				const data = $mol_wire_sync( this ).history_migration_data( source.data, origin )
				const event = history.event_write( data, source.time, source.author, source.peer, source.targets )
				relocated.set( source.link, event.link().str )
			}

			map.History_legacy( null )!.remote( legacy )
			map.History( null )!.remote( history )
			this.clear_legacy_history( legacy )
			history.storage_migrated( true )
			map.History_legacy( null )!.remote( null )
		}

		static history_migration_data( data: $shm_hitalama_map_history_data, origin?: string ) {
			return origin === data.origin ? data : { ...data, origin }
		}

		/** Snapshot before writes, and put referenced originals before controls. */
		static history_migration_snapshot( legacy: $shm_hitalama_map_history ) {
			const sources = legacy.events().map( event=> {
				const data = event.data()
				if( !data ) throw new Error( 'Не загружено событие для переноса истории' )
				return { link: event.link().str, data, time: event.time(),
					author: event.author(), peer: event.peer(), targets: event.targets() }
			})
			const by_link = new Map( sources.map( source=> [ source.link, source ] ) )
			const ordered: typeof sources = [], visited = new Set< string >, visiting = new Set< string >
			const visit = ( source: typeof sources[number] )=> {
				if( visited.has( source.link ) ) return
				if( visiting.has( source.link ) ) throw new Error( 'Циклические ссылки в истории изменений' )
				visiting.add( source.link )
				const original = source.data.origin && by_link.get( source.data.origin )
				if( original ) visit( original )
				visiting.delete( source.link )
				visited.add( source.link )
				ordered.push( source )
			}
			for( const source of sources ) visit( source )
			return ordered
		}

		@ $mol_action
		static migrate_history_storage( map: $shm_hitalama_map ) {
			// Legacy links stay readable through bounded pagination. Backfilling every
			// immutable event here would make opening an old map wait for the whole
			// history. New events use v2 immediately; an explicit maintenance pass may
			// compact the legacy tail later without blocking a user navigation.
			void map
		}

		@ $mol_action
		static migrate_history_storage_meta( map: $shm_hitalama_map ) {
			const history = map.History()?.remote()
			if( !history ) return
			history.land().sync()
			history.storage_writers_migrate()
			// Only mark an old history complete when the old authoritative marker
			// says so, or when it never contained a legacy tail. Incomplete histories
			// must retain their compatibility fallback until explicit migration.
			if( history.Legacy_migrated()?.val() || !( history.Events()?.remote_list().length ?? 0 ) ) {
				history.storage_migrated( true )
			}
		}

		@ $mol_action
		static migrate_history_reference( map: $shm_hitalama_map ) {
			map.history_reference_publish()
		}

		@ $mol_action
		static clear_legacy_history( history: $shm_hitalama_map_history ) {
			for( const event of history.events() ) {
				event.Data( null )?.val({})
				event.Targets( null )?.items([])
				event.Author( null )?.val( '' )
				event.Peer( null )?.val( '' )
				event.Time( null )?.val( 0 )
				for( const key of event.keys() ) event.has( key, false )
			}

			for( const target of history.By_target()?.keys() ?? [] ) {
				history.By_target()?.key( target )?.items([])
				history.By_target()?.has( target, false )
			}
			history.Events( null )?.items([])
			for( const key of history.keys() ) history.has( key, false )
		}

		static legacy_dive< Pawn extends typeof $giper_baza_pawn >(
			widget: $shm_hitalama_map_widget,
			key: string,
			Pawn: Pawn,
		) {
			if( !widget.has( key ) ) return null
			return widget.dive( key, Pawn ) as InstanceType< Pawn > | null
		}

		static legacy_text( widget: $shm_hitalama_map_widget, key: string ) {
			return this.legacy_dive( widget, key, $giper_baza_text )?.value()
		}

		static legacy_real( widget: $shm_hitalama_map_widget, key: string ) {
			return this.legacy_dive( widget, key, $giper_baza_atom_real )?.val()
		}

		static legacy_bool( widget: $shm_hitalama_map_widget, key: string ) {
			return this.legacy_dive( widget, key, $giper_baza_atom_bool )?.val()
		}

		static legacy_link< Pawn extends typeof $giper_baza_pawn >(
			widget: $shm_hitalama_map_widget,
			key: string,
			Pawn: Pawn,
		): InstanceType< Pawn > | null {
			return ( this.legacy_dive( widget, key, $giper_baza_atom_link.to( ()=> Pawn ) )?.remote() ?? null ) as InstanceType< Pawn > | null
		}

		@ $mol_action
		static migrate_widgets_entities( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				this.migrate_widget_style( widget )
				this.migrate_widget_code_entities( widget )
				this.migrate_widget_value_entities( widget )
				this.migrate_widget_link_entities( widget )
			}
		}

		static migrate_widget_style( widget: $shm_hitalama_map_widget ) {
			const bgcolor = this.legacy_dive( widget, 'Bgcolor', $giper_baza_atom_text )?.val()
			if( bgcolor && !widget.Background_color()?.val() ) {
				widget.Background_color(null)?.val( bgcolor )
			}

			const opacity = this.legacy_dive( widget, 'Background_opacity', $giper_baza_atom_text )?.val()
			if( opacity !== undefined && widget.Background_opacity()?.val() === null ) {
				widget.Background_opacity(null)?.val( Number( opacity ) )
			}
		}

		static migrate_widget_code_entities( widget: $shm_hitalama_map_widget ) {
			const subtext = this.legacy_text( widget, 'Subtext' )

			switch( widget.type() ) {
				case 'code_sql':
					if( subtext && !widget.Code_sql()?.remote()?.Sql()?.value() ) {
						widget.code_sql().sql( subtext )
					}
					break
				case 'text':
				case 'text_md':
					if( subtext && !widget.Link_to_viewport()?.val() ) {
						widget.link_to_viewport( subtext )
					}
					break
			}
		}

		static migrate_widget_value_entities( widget: $shm_hitalama_map_widget ) {
			this.migrate_widget_chart( widget )

			const ratio = this.legacy_real( widget, 'Ratio' )
			if( ratio !== null && ratio !== undefined && widget.Viewport_frame()?.remote()?.Ratio()?.val() === null ) {
				widget.viewport_frame().ratio( ratio )
			}

			const hide_buttons = this.legacy_bool( widget, 'Hide_buttons' )
			if( hide_buttons !== null && hide_buttons !== undefined && widget.Form_custom()?.remote()?.Hide_buttons()?.val() === null ) {
				widget.form_custom().hide_buttons( hide_buttons )
			}
		}

		static migrate_widget_chart( widget: $shm_hitalama_map_widget ) {
			const legacy = this.legacy_dive( widget, 'Chart', $shm_hitalama_map_widget_entity_chart )
			if( !legacy ) return
			if( widget.Chart()?.remote() ) return

			const chart = widget.map().Charts(null)?.make( null )
			if( !chart ) return

			chart.Widget(null)?.remote( widget )
			chart.Axis( null )?.val( legacy.Axis()?.val() )
			chart.Values( null )?.val( legacy.Values()?.val() )
			chart.Groups( null )?.val( legacy.Groups()?.val() )
			chart.Filters_enabled( null )?.val( legacy.Filters_enabled()?.val() )
			chart.Gap_left( null )?.val( legacy.Gap_left()?.val() )

			legacy.Axis_details()?.keys().forEach( key => {
				chart.Axis_details( null )?.key( key, 'auto' )?.val( legacy.Axis_details()?.key( key )?.val() )
			} )
			legacy.Filters_options()?.keys().forEach( key => {
				chart.Filters_options( null )?.key( key, 'auto' )?.val( legacy.Filters_options()?.key( key )?.val() )
			} )

			widget.has( 'Chart', false )
			widget.Chart(null)?.remote( chart )
		}

		static migrate_widget_link_entities( widget: $shm_hitalama_map_widget ) {
			const use_chart_from = this.legacy_link( widget, 'Use_chart_from', $shm_hitalama_map_widget )
			if( use_chart_from && !widget.Chart()?.remote() ) {
				const chart = use_chart_from.chart()
				if( chart ) widget.Chart(null)?.remote( chart )
			}
		}

		static migrate_chart_settings_widgets( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				if( widget.Type()?.vary() !== 'chart_settings' ) continue

				const chart = widget.Chart()?.remote()
				const chart_widget = chart?.Widget()?.remote()

				widget.Type(null)?.val( 'settings_chart' )

				if( chart_widget ) {
					widget.controlled_widget( chart_widget )
					chart_widget.settings_widget( widget )
				}

				widget.has( 'Chart', false )
			}
		}

		@ $mol_action
		static migrate_method_runtime_this( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				if( widget.type() !== 'method' ) continue

				const code = widget.code()
				const next = code.replace( /(^|[^\w$.])map\s*\./g, ( str: string, head: string )=> `${ head }this.` )
				if( next === code ) continue

				widget.code( next )
			}
		}

		@ $mol_action
		static migrate_inside_widget_to_container_widget( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				const container = this.legacy_link( widget, 'Inside_widget', $shm_hitalama_map_widget )
				if( container && !widget.Container_widget()?.remote() ) {
					widget.Container_widget(null)?.remote( container )
				}

				widget.has( 'Inside_widget', false )
			}
		}

		@ $mol_action
		static migrate_widget_border_styles( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				switch( widget.type() ) {
					case 'layout_vert':
					case 'layout_hor':
					case 'viewport_frame':
						this.migrate_widget_border_preset( widget, 'var(--mol_theme_line)', 2, 'dashed' )
						break
					case 'group':
						this.migrate_widget_border_preset( widget, 'oklch( 60% 0 var(--mol_theme_hue) / .1 )', 1, 'dashed' )
						break
				}
			}
		}

		static migrate_widget_border_preset(
			widget: $shm_hitalama_map_widget,
			color: string,
			width: number,
			style: string,
		) {
			if( !widget.Border_color()?.val() ) widget.Border_color(null)?.val( color )
			if( widget.Border_width()?.val() == null ) widget.Border_width(null)?.val( width )
			if( !widget.Border_style()?.val() ) widget.Border_style(null)?.val( style )
		}

		@ $mol_action
		static migrate_image_widgets( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				if( widget.type() !== 'text' ) continue
				if( !widget.Image()?.blob() ) continue
				widget.Type(null)?.val( 'image' )
			}
		}

		@ $mol_action
		static migrate_runtime_context_dollar( map: $shm_hitalama_map ) {
			for( const widget of this.widgets_all( map ) ) {
				this.migrate_widget_runtime_context_dollar( widget )
				this.migrate_widget_data_method_context_dollar( widget )
				this.migrate_widget_table_methods_context_dollar( widget )
			}
		}

		static migrate_widget_runtime_context_dollar( widget: $shm_hitalama_map_widget ) {
			if( !this.runtime_context_code_widget_types().has( widget.type() ) ) return

			const code = widget.code()
			const next = this.migrate_runtime_context_code( code )
			if( next === code ) return

			widget.code( next )
		}

		static migrate_widget_data_method_context_dollar( widget: $shm_hitalama_map_widget ) {
			const atom = widget.Data_method?.()
			const code = atom?.val()
			if( !code ) return

			const next = this.migrate_runtime_context_code( code )
			if( next === code ) return

			atom?.val( next )
		}

		static migrate_widget_table_methods_context_dollar( widget: $shm_hitalama_map_widget ) {
			const table = widget.Table?.()?.remote()
			if( !table ) return

			this.migrate_atom_text_runtime_context_dollar( table.Head_method?.() )
			this.migrate_atom_text_runtime_context_dollar( table.Rows_method?.() )
		}

		static migrate_atom_text_runtime_context_dollar( atom: { val( next?: string ): string | null | undefined } | null | undefined ) {
			const code = atom?.val()
			if( !code ) return

			const next = this.migrate_runtime_context_code( code )
			if( next === code ) return

			atom?.val( next )
		}

		@ $mol_action
		static migrate_opencode_session_ids( map: $shm_hitalama_map ) {
			const session_id = map.store( 'opencode_session_id' )
			if( typeof session_id !== 'string' || !session_id ) return

			const ids = this.opencode_session_ids_store( map )
			if( ids.includes( session_id ) ) return

			map.store( 'opencode_session_ids', [ session_id, ... ids ] )
		}

		static opencode_session_ids_store( map: $shm_hitalama_map ) {
			const value = map.store( 'opencode_session_ids' )
			if( Array.isArray( value ) ) return value.filter( item => typeof item === 'string' && item )
			if( typeof value === 'string' ) {
				try {
					const parsed = JSON.parse( value )
					if( Array.isArray( parsed ) ) return parsed.filter( item => typeof item === 'string' && item )
				} catch( error ) {
					return []
				}
			}
			return []
		}

		static runtime_context_code_widget_types() {
			return new Set([
				'code',
				'code_js',
				'code_ts',
				'code_html',
				'method',
				'component',
				'component_view',
				'customdom',
			])
		}

		static runtime_context_replacements() {
			return {
				map: '$' + 'map',
				page: '$' + 'page',
				this_widget: '$' + 'this_widget',
				view: '$' + 'view',
				arg: '$' + 'arg',
				next: '$' + 'next',
				table: '$' + 'table',
				to_table: '$' + 'to_table',
				create_molwire_dom: '$' + 'create_molwire_dom',
				cors_proxy: '$' + 'cors_proxy',
			} as Record< string, string >
		}

		static migrate_runtime_context_code( code: string ) {
			const declared = this.runtime_context_declared_names( code )
			const replacements = this.runtime_context_replacements()
			const id_start = /[A-Za-z_$]/
			const id_body = /[A-Za-z0-9_$]/

			const migrate = ( from: number, end = '' )=> {
				let out = ''
				let index = from

				while( index < code.length ) {
					const char = code[ index ]
					const next = code[ index + 1 ]
					if( end && char === end ) return { out, index }

					if( char === '\'' || char === '"' ) {
						const res = this.migrate_runtime_context_copy_string( code, index, char )
						out += res.out
						index = res.index
						continue
					}

					if( char === '`' ) {
						const res = migrate_template( index )
						out += res.out
						index = res.index
						continue
					}

					if( char === '/' && next === '/' ) {
						const res = this.migrate_runtime_context_copy_line_comment( code, index )
						out += res.out
						index = res.index
						continue
					}

					if( char === '/' && next === '*' ) {
						const res = this.migrate_runtime_context_copy_block_comment( code, index )
						out += res.out
						index = res.index
						continue
					}

					if( char === '{' ) {
						const res = migrate( index + 1, '}' )
						out += `{${ res.out }`
						index = res.index
						if( code[ index ] === '}' ) {
							out += '}'
							++index
						}
						continue
					}

					if( id_start.test( char ) ) {
						let finish = index + 1
						while( finish < code.length && id_body.test( code[ finish ] ) ) ++finish

						const word = code.slice( index, finish )
						out += this.runtime_context_replace_allowed( code, index, finish, word, declared )
							? replacements[ word ]
							: word
						index = finish
						continue
					}

					out += char
					++index
				}

				return { out, index }
			}

			const migrate_template = ( from: number )=> {
				let out = '`'
				let index = from + 1

				while( index < code.length ) {
					const char = code[ index ]
					const next = code[ index + 1 ]

					if( char === '\\' ) {
						out += code.slice( index, index + 2 )
						index += 2
						continue
					}

					if( char === '`' ) {
						out += '`'
						return { out, index: index + 1 }
					}

					if( char === '$' && next === '{' ) {
						const res = migrate( index + 2, '}' )
						out += '${' + res.out
						index = res.index
						if( code[ index ] === '}' ) {
							out += '}'
							++index
						}
						continue
					}

					out += char
					++index
				}

				return { out, index }
			}

			return migrate( 0 ).out
		}

		static runtime_context_replace_allowed(
			code: string,
			start: number,
			finish: number,
			word: string,
			declared: Set< string >,
		) {
			if( declared.has( word ) ) return false
			if( !( word in this.runtime_context_replacements() ) ) return false

			const prev = this.runtime_context_prev_token( code, start )
			if( prev === '.' ) return false

			const next = this.runtime_context_next_token( code, finish )
			if( next === ':' ) return false

			return true
		}

		static runtime_context_prev_token( code: string, index: number ) {
			for( let pos = index - 1; pos >= 0; --pos ) {
				if( /\s/.test( code[ pos ] ) ) continue
				return code[ pos ]
			}
			return ''
		}

		static runtime_context_next_token( code: string, index: number ) {
			for( let pos = index; pos < code.length; ++pos ) {
				if( /\s/.test( code[ pos ] ) ) continue
				return code[ pos ]
			}
			return ''
		}

		static runtime_context_declared_names( code: string ) {
			const names = new Set< string >()
			const plain = this.runtime_context_code_without_literals( code )
			const keys = Object.keys( this.runtime_context_replacements() )
			const name = `(${ keys.join( '|' ) })`

			for( const match of plain.matchAll( new RegExp( `\\b(?:const|let|var|function|class)\\s+${ name }\\b`, 'g' ) ) ) {
				names.add( match[1] )
			}

			for( const match of plain.matchAll( new RegExp( `\\bcatch\\s*\\(\\s*${ name }\\b`, 'g' ) ) ) {
				names.add( match[1] )
			}

			for( const match of plain.matchAll( /\bfunction\b[^(]*\(([^)]*)\)/g ) ) {
				this.runtime_context_declared_params( match[1] ).forEach( param => names.add( param ) )
			}

			for( const match of plain.matchAll( /\(([^)]*)\)\s*=>/g ) ) {
				this.runtime_context_declared_params( match[1] ).forEach( param => names.add( param ) )
			}

			for( const match of plain.matchAll( new RegExp( `\\b${ name }\\s*=>`, 'g' ) ) ) {
				names.add( match[1] )
			}

			return names
		}

		static runtime_context_declared_params( params: string ) {
			const names = new Set< string >()
			const replacements = this.runtime_context_replacements()

			for( const param of params.split( ',' ) ) {
				const match = param.trim().match( /^([A-Za-z_$][A-Za-z0-9_$]*)\b/ )
				if( match && match[1] in replacements ) names.add( match[1] )
			}

			return names
		}

		static runtime_context_code_without_literals( code: string ) {
			let out = ''
			let index = 0

			while( index < code.length ) {
				const char = code[ index ]
				const next = code[ index + 1 ]

				if( char === '\'' || char === '"' || char === '`' ) {
					const res = this.migrate_runtime_context_copy_string( code, index, char )
					out += ' '.repeat( res.out.length )
					index = res.index
					continue
				}

				if( char === '/' && next === '/' ) {
					const res = this.migrate_runtime_context_copy_line_comment( code, index )
					out += ' '.repeat( res.out.length )
					index = res.index
					continue
				}

				if( char === '/' && next === '*' ) {
					const res = this.migrate_runtime_context_copy_block_comment( code, index )
					out += ' '.repeat( res.out.length )
					index = res.index
					continue
				}

				out += char
				++index
			}

			return out
		}

		static migrate_runtime_context_copy_string( code: string, from: number, quote: string ) {
			let out = quote
			let index = from + 1

			while( index < code.length ) {
				const char = code[ index ]
				out += char
				++index

				if( char === '\\' ) {
					out += code[ index ] ?? ''
					++index
					continue
				}

				if( char === quote ) break
			}

			return { out, index }
		}

		static migrate_runtime_context_copy_line_comment( code: string, from: number ) {
			const finish = code.indexOf( '\n', from )
			const index = finish === -1 ? code.length : finish
			return { out: code.slice( from, index ), index }
		}

		static migrate_runtime_context_copy_block_comment( code: string, from: number ) {
			const finish = code.indexOf( '*/', from + 2 )
			const index = finish === -1 ? code.length : finish + 2
			return { out: code.slice( from, index ), index }
		}

	}

}
